import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  ApplyCustomButtonLinkFailure,
  ApplyCustomButtonLinkResponse,
  CustomButtonLinkScope
} from "types/landlord/address";
import { targetLabel, type PropertyDictionary } from "./helpers";

const REASON_LABELS: Record<string, string> = {
  not_found: "Not found or not eligible for bulk apply",
  validation_failed: "Validation error"
};

const getReasonText = (failure: ApplyCustomButtonLinkFailure) =>
  failure.message || REASON_LABELS[failure.reason] || failure.reason;

const getTitle = ({
  jobFailedOutright,
  hasFailures
}: {
  jobFailedOutright: boolean;
  hasFailures: boolean;
}) => {
  if (jobFailedOutright) return "Could not apply custom button link";
  if (hasFailures) return "Custom button link applied with some errors";

  return "Custom button link applied";
};

const getDescription = ({
  result,
  scope,
  jobFailedOutright,
  hasFailures,
  dictionary
}: {
  result: ApplyCustomButtonLinkResponse;
  scope: CustomButtonLinkScope;
  jobFailedOutright: boolean;
  hasFailures: boolean;
  dictionary: PropertyDictionary;
}) => {
  if (jobFailedOutright)
    return (
      result.error ||
      "Something went wrong while applying the link. Please try again."
    );

  const parts = [
    `The link was applied to ${result.updated_count} ${targetLabel(result.updated_count, scope, dictionary)}.`
  ];

  if (result.pms_switched_count)
    parts.push(
      `${result.pms_switched_count} ${targetLabel(result.pms_switched_count, scope, dictionary)} were switched from PMS sync to a manually-entered link.`
    );

  if (hasFailures)
    parts.push(
      `${result.failed.length} ${targetLabel(result.failed.length, scope, dictionary)} could not be updated.`
    );

  return parts.join(" ");
};

export const ApplyCustomButtonLinkResultDialog = ({
  result,
  scope,
  onClose
}: {
  result: ApplyCustomButtonLinkResponse | null;
  scope: CustomButtonLinkScope;
  onClose: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  if (!result) return null;

  const jobFailedOutright = result.status === "failed";
  const hasFailures = result.failed.length > 0;

  return (
    <Modal onClose={onClose} opened panelClassName="max-w-[520px] w-full">
      <header className="border-b border-alpha-40 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-bold text-content-1000">
            {getTitle({ jobFailedOutright, hasFailures })}
          </h3>
          <IconButton
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            size={IconButtonSize.BASE}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>
        <p className="mt-2 text-sm text-content-400">
          {getDescription({
            result,
            scope,
            jobFailedOutright,
            hasFailures,
            dictionary
          })}
        </p>
      </header>

      {hasFailures ? (
        <div className="max-h-[280px] space-y-2 overflow-y-auto border-b border-alpha-40 px-6 py-4">
          {result.failed.map((failure) => (
            <div key={`failed-${failure.target_id}`}>
              <p className="truncate text-sm font-medium text-content-700">
                {failure.target_name ||
                  `${targetLabel(1, scope, dictionary)} #${failure.target_id}`}
              </p>
              <p className="text-xs text-danger-500">
                {getReasonText(failure)}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <footer className="flex items-center justify-end px-6 py-4">
        <Button onClick={onClose} variant={ButtonVariant.PRIMARY}>
          Done
        </Button>
      </footer>
    </Modal>
  );
};
